import { useEffect } from "react";
import { APP_VERSION } from "./version";
import { startHoverPrefetch } from "./utils/prefetch";
import { onForegroundMessage } from "./firebase";
import { navigate } from "./utils/navigation";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { MessageBoxProvider } from "./context/MessageBoxContext";
import { ThemeProvider } from "./context/ThemeContext";
import { GameInviteProvider } from "./context/GameInviteContext";
import { CardSharksToaster } from "./components/UI/CardSharksToast";
import { PushNotificationModal } from "./components/Features/PushNotificationModal";
import { FeatureGraphicPopupContainer } from "./components/Features/FeatureGraphicPopupContainer";
import { RouterProvider } from "react-router-dom";
import { router } from "./router";
import { getApiUrl } from "./config/apiConfig";
import { usePushNotifications } from "./hooks/features/usePushNotifications";
import { rulesetConfigService } from "./arena/services/RulesetConfigService";
import posthog from "posthog-js";
import { PostHogProvider } from "@posthog/react";

if (import.meta.env.VITE_POSTHOG_PROJECT_TOKEN) {
  posthog.init(import.meta.env.VITE_POSTHOG_PROJECT_TOKEN, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
    defaults: "2026-01-30",
    capture_pageview: false,
    autocapture: false,
    disable_session_recording: true,
    advanced_disable_decide: true,
    disable_surveys: true,
    persistence: 'localStorage',
    on_xhr_error: () => {},
  });
}

function AppContent() {
  const { user, loading } = useAuth();
  const { shouldPrompt, dismissPrompt } = usePushNotifications(user);

  useEffect(() => {
    const appVersionKey = 'acs_app_version';
    const storedVersion = localStorage.getItem(appVersionKey);
    if (storedVersion && storedVersion !== APP_VERSION) {
      console.log(`[AppUpdate] Version updated from ${storedVersion} to ${APP_VERSION}. Purging stale cache...`);
      import('./services/loyaltyApi').then(({ loyaltyApi }) => {
        loyaltyApi.invalidateLoyaltySummaryCache();
      }).catch(() => {});
      try {
        sessionStorage.clear();
      } catch {}
    }
    localStorage.setItem(appVersionKey, APP_VERSION);
  }, []);

  useEffect(() => {
    rulesetConfigService.fetchAllRulesetConfigs().catch(() => {});
    return startHoverPrefetch();
  }, []);

  // Catch dynamic CSS chunk failures caused by client-server build hash mismatches
  useEffect(() => {
    const handleResourceError = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (target && target.tagName === 'LINK' && target.getAttribute('rel') === 'stylesheet') {
        const href = target.getAttribute('href') || '';
        if (href.includes('chunk') || href.includes('/static/css/')) {
          console.warn(`[PWA] Dynamic CSS chunk failed to load (${href}). Reloading to apply fresh build...`);
          const reloadKey = 'chunk_css_reload';
          const lastReload = sessionStorage.getItem(reloadKey);
          const now = Date.now();
          if (!lastReload || now - parseInt(lastReload, 10) > 15000) {
            sessionStorage.setItem(reloadKey, now.toString());
            window.location.reload();
          }
        }
      }
    };

    window.addEventListener('error', handleResourceError, true);
    return () => window.removeEventListener('error', handleResourceError, true);
  }, []);

  // Listen for messages posted from the service worker
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker) return;
    const handler = (event: any) => {
      const data = event?.data;
      if (!data) return;
      if (data.type === 'NAVIGATE') {
        if (data.url) {
          let targetPath = data.url;
          try {
            // Strip origin if the URL is absolute and matches our current origin
            const parsedUrl = new URL(data.url, window.location.origin);
            if (parsedUrl.origin === window.location.origin) {
              targetPath = parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
            }
          } catch (e) {
            console.error("Failed to parse navigation URL:", e);
          }
          navigate(targetPath);
        }
        if (data.focusComposer || data.replyText) {
          window.dispatchEvent(new CustomEvent('push:focusComposer', { detail: { threadId: data.threadId, focusComposer: !!data.focusComposer, replyText: data.replyText } }));
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, []);

  // Warm up the Cloud Run API backend and Firestore connection to mitigate serverless cold start
  useEffect(() => {
    fetch(getApiUrl('/api/diagnostics/warmup')).catch(() => {});
  }, []);

  // Foreground FCM message handler
  useEffect(() => {
    if (!user) return;
    let unsub: (() => void) | undefined;
    let isMounted = true;

    onForegroundMessage((payload: any) => {
      console.log('[FCM] Foreground message received:', payload);
      const notification = payload.notification || {};
      const data = payload.data || {};

      if (data.type === 'APP_UPDATE') {
        console.log('[FCM] Foreground APP_UPDATE message received. Triggering update prompt...');
        window.dispatchEvent(new Event('app-update-ready'));
        return;
      }

      const threadId = data.threadId || '';

      const currentPath = window.location.pathname + window.location.search;
      const isVisibleAndFocused = document.visibilityState === 'visible' && document.hasFocus();
      
      // Suppress if user is actively viewing this thread
      if (threadId && currentPath.includes(`threadId=${threadId}`) && isVisibleAndFocused) return;

      const title = notification.title || data.title || 'ATL Card Sharks';
      const body  = notification.body  || data.body  || 'You have a new notification.';

      // Play sound
      try {
        const audio = new Audio('/Images/notify.mp3');
        audio.volume = 0.8;
        audio.play().catch(() => {}); 
      } catch (_) {}
      
      // Show system notification via service worker so it appears consistently
      navigator.serviceWorker.ready.then((reg) => {
        reg.showNotification(title, {
          body,
          icon: '/Images/icon.png',
          badge: '/Images/icon.png',
          tag: threadId || 'acs-notification',
          renotify: true,
          vibrate: [200, 100, 200],
          data: { ...data, threadId },
        } as any);
      });
    }).then((fn: any) => {
      if (isMounted) {
        unsub = fn;
      } else if (fn) {
        fn();
      }
    });
    return () => { 
      isMounted = false;
      if (unsub) unsub(); 
    };
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0a0d14]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-acs-red"></div>
      </div>
    );
  }

  return (
    <>
      <CardSharksToaster />
      
      <PostHogProvider client={posthog}>
        <RouterProvider router={router} />
      </PostHogProvider>

      {shouldPrompt && (
        <PushNotificationModal onClose={() => dismissPrompt()} />
      )}

      <FeatureGraphicPopupContainer />
    </>
  );
}

function Main() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <MessageBoxProvider>
          <GameInviteProvider>
            <AppContent />
          </GameInviteProvider>
        </MessageBoxProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default Main;
